import { NO, UIColor, UIRoute, UITextView, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHPublishedProcurementStatus } from "../../../models/prodModels"
import { RHPublishedProcurementChipKey, RHPublishedProcurementsQuery } from "../../../models/PublishedProcurementSearch"
import { RHCore } from "../Core/RHCore"
import { RHProcurementStore } from "../Core/RHProcurementStore"
import {
    CBColumnDescriptor,
    CBDataViewCellDescriptor,
    CBDataViewCellDescriptorWithObject,
    isCBSelectionColumnDescriptor
} from "../Custom components/CB/CBDataView"
import { RHButton } from "../Custom components/RH/RHButton"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHDataView, RHNumberSorting } from "../Custom components/RH/RHDataView"
import { RHFavoriteColumn } from "../Custom components/RH/RHFavoriteColumn"
import { RHFilterChipBarView } from "../Custom components/RH/RHFilterChipBarView"
import { RHFilterChipButton } from "../Custom components/RH/RHFilterChipButton"
import { RHFilterSelection } from "../Custom components/RH/RHFilterSelection"
import {
    RHDateRangeCriterion,
    RHFilterCriteria,
    RHFilterPanelView,
    RHRangeCriterion,
    RHTextCriterion
} from "../Custom components/RH/RHFilterPanelView"
import { RHTableActionButtonView } from "../Custom components/RH/RHTableActionButtonView"
import { RHCPVCodeFormatter } from "../Custom components/RH/Utils_slop/RHCPVCodeFormatter"
import { formatRHDateTimeWithLineBreak } from "../Custom components/RH/Utils_slop/RHDateFormatting"
import { RHBadges, RHButtons, RHSpacing, RHTables } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHEuroFormatter } from "../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHProcurementLifecyclePresentation } from "../Custom components/RH/Utils_slop/RHProcurementLifecyclePresentation"
import { RHTextSanitizer } from "../Custom components/RH/Utils_slop/RHTextSanitizer"
import { RHProcurementSearchViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { CPVTreeDialogView } from "../NewProcurement/CPVTreeDialogView"
import { ProcurementDetailsViewController } from "../ProcurementDetails/ProcurementDetailsViewController"
import type { RHPublishedProcurementListRow, RHPublishedProcurementsPage } from "../SocketClientFunctions"


type ProcurementSearchCellView = Parameters<NonNullable<CBDataViewCellDescriptor<ProcurementSearchDataItem>["updateCell"]>>[0]

// The store's own object for the procurement, so whatever is loaded into it later shows up here too.
type ProcurementSearchRow = RHPublishedProcurementListRow & {
    isPlaceholder?: undefined
}

// Stands in for a row whose page has not arrived yet, so the table is as tall as every match from the start.
type ProcurementSearchPlaceholderRow = {
    _id: string
    isPlaceholder: typeof YES
}

type ProcurementSearchDataItem = ProcurementSearchRow | ProcurementSearchPlaceholderRow

// Public lifecycle-state filters. Drafts are private, so the global search has
// no "Ettevalmistamisel" chip. The broad "lopetatud" key covers both server
// lifecycle states shown to the user as "Lõpetatud".
type StatusFilterKey =
    | "started"
    | "evaluating"
    | "lopetatud"
    | "inExecution"
    | "executed"

type ProcurementSearchQuickFilterKey = StatusFilterKey | "favorites"

// The server separately adds the viewer's own-company drafts to this published status selection.
const PUBLISHED_STATUSES = [
    "published",
    "evaluation-finalized",
    "closed-no-winner",
    "closed-without-contract"
] satisfies RHPublishedProcurementStatus[]

// Eleven visible columns, mirroring the dashboard's column rhythm:
// lemmik, Viitenumber, Nimetus, Ettevõte, CPV, Eeldatav maksumus,
// Avaldatud, Tähtaeg, Osade arv, Seisund, Vaata. Nimetus stays widest and
// Ettevõte receives its own readable column without taking width from the
// minimum-sized status and action columns.
// Rows fetched per server request; a page of rows on each side of the viewport is loaded ahead of need.
const PROCUREMENT_SEARCH_PAGE_SIZE = 50

// Typing and quick successive filter changes collapse into one server request.
const PROCUREMENT_SEARCH_QUERY_DELAY_MILLISECONDS = 250

const PROCUREMENT_SEARCH_COLUMN_WEIGHTS = [0.45, 1, 2.2, 1.45, 1.1, 1.1, 1.1, 1, 0.75, 1.35, 0.85]


/**
 * ProcurementSearchViewController — the GLOBAL procurement search ("Kõik hanked"),
 * shown to both public visitors and logged-in users. It lists every company's
 * visible published procurements, newest published first, with shared lifecycle
 * status chips. Signed-in users additionally see drafts and directed procurements
 * from their own procurer companies; the server authorizes this scope.
 *
 * The set can be large, so the server owns the full query (search, sorting, chips, panel, CPV)
 * and the table loads a page at a time. The table is as tall as the number of matching rows from
 * the first response on; rows whose page is not loaded yet are blank, and the pages in or near
 * the viewport are requested as the user scrolls or drags to them.
 */
export class ProcurementSearchViewController extends UIViewController {

    static override readonly routeComponentName = "procurement_search"
    static override readonly ParameterIdentifierName = {
        searchText: "searchText"
    }

    // ── Title ────────────────────────────────────────────────────────────────

    readonly titleLabel = new UITextView(
        this.view.elementID + "TitleLabel",
        UITextView.type.header2
    ).configuredWithObject({
        text: "Hankeotsing",
        backgroundColor: UIColor.transparentColor
    }).addedAsSubviewToView(this.view)

    // ── Status filter chips (flat, multi-select; wrap via RHFilterChipBarView) ──

    readonly filterAllButton            = ProcurementSearchViewController._makeFilterChip("Kõik hanked")
    readonly filterStartedButton        = ProcurementSearchViewController._makeFilterChip("Alustatud")
    readonly filterEvaluationButton     = ProcurementSearchViewController._makeFilterChip("Hindamisel")
    readonly filterFinalizedButton      = ProcurementSearchViewController._makeFilterChip("Lõpetatud")
    readonly filterInExecutionButton    = ProcurementSearchViewController._makeFilterChip("Täitmisel")
    readonly filterExecutedButton       = ProcurementSearchViewController._makeFilterChip("Teostatud")

    // Sits last in the chip row; opens the "Muud filtrid" panel below. Not a status
    // filter — it carries the funnel + caret icon and a neutral "soft" (open) look,
    // mirroring the panel's open state via isExpandedDidChange.
    readonly moreFiltersButton = ProcurementSearchViewController._makeFilterChip("Muud filtrid")

    readonly filterFavoritesButton = ProcurementSearchViewController._makeFilterChip("Lemmikud")

    // The wrapping chip bar — owns + lays out the visible status chips plus
    // "Muud filtrid". Its chip set is refreshed per data source (own vs public).
    readonly filterChipBar = new RHFilterChipBarView(this.view.elementID + "FilterChipBar")
        .addedAsSubviewToView(this.view)

    // ── Data view ────────────────────────────────────────────────────────────

    readonly dataView = new RHDataView<ProcurementSearchDataItem>(
        this.view.elementID + "DataView"
    ).configuredWithObject({
        noDataMessage: "Hankeid ei leitud.",
        rowsAreInDisplayOrder: YES,
        tableView: {
            allRowsHaveEqualHeight: YES
        }
    }).addedAsSubviewToView(this.view)

    // ── Filter panel ─────────────────────────────────────────────────────────

    // Reusable collapsible "Filtrid" panel; emits an RHFilterCriteria the controller
    // folds into the paged server query. Number/date ranges only — no per-procurer counts here.
    readonly filterPanel = new RHFilterPanelView(this.view.elementID + "FilterPanel")
        .configuredWithObject({
            usesExternalToggle: YES,
            fields: [
                {
                    key: "procuringCompanyName", label: "Hankija", kind: "text",
                    autocompleteStringsProvider: () => this._procuringCompanyNames
                },
                { key: "responsibleProcuringUser", label: "Hanke eest vastutav isik", kind: "text" },
                { key: "estimatedCost", label: "Eeldatav maksumus (KM-ta)", kind: "numberRange", allowDecimals: YES },
                { key: "publishedAt", label: "Avaldatud", kind: "dateRange" },
                { key: "deadlineDate", label: "Tähtaeg", kind: "dateRange" }
            ]
        }).addedAsSubviewToView(this.view)

    // ── CPV search ─────────────────────────────────────────────────────────────

    // Small blue "Otsi CPV-koodi järgi" button (+ a reset button when active) shown
    // directly under the table's search bar (installed as the data view's search
    // accessory). Opens the shared CPV tree picker in multi-select mode; the chosen
    // codes filter the results to procurements whose parts carry one of those CPV
    // codes (or a descendant). Reuses the exact same picker the new-procurement editor
    // uses to pick a part's CPV.
    readonly cpvSearchBar = new CPVSearchBarView(this.view.elementID + "CPVSearchBar")

    readonly cpvTreeDialog = new CPVTreeDialogView()

    // ── Internal state ───────────────────────────────────────────────────────

    // Rows of the latest requested query by their index in the server's order; pages not loaded yet are absent.
    _loadedRowsByIndex = new Map<number, ProcurementSearchRow>()
    _loadedPageIndexes = new Set<number>()
    _pageIndexesLoading = new Set<number>()
    // How many rows match the latest requested query in all, loaded or not; sets the table's height.
    _totalProcurements = 0
    _placeholderRows: ProcurementSearchPlaceholderRow[] = []
    _visibleRowRange?: { first: number, last: number }
    _isReloading = NO
    readonly _quickFilters = new RHFilterSelection<ProcurementSearchQuickFilterKey>()
    _loadGeneration = 0
    _filterCriteria: RHFilterCriteria = {}
    _chipCounts?: Record<RHPublishedProcurementChipKey, number>
    // From the server, so the suggestions cover every procurer and not just those on the loaded pages.
    _procuringCompanyNames: string[] = []
    _latestRequestedQuery?: RHPublishedProcurementsQuery
    _serverQueryTimer?: ReturnType<typeof setTimeout>
    // Filter changes made while the view is away must not start loads; viewWillAppear loads afresh.
    _isViewActive = NO
    // The loading overlay disables the data view, search box included, so it only covers the first load.
    _hasLoadedProcurements = NO
    // CPV codes selected through the tree picker (raw code tokens, e.g. "03110000-5").
    // Empty = no CPV filter active. A row passes if any of its parts' CPV codes match
    // ANY selected code (OR-logic, hierarchical).
    _cpvFilterCodes: string[] = []


    constructor(view: UIView) {
        super(view)

        this.view.configureWithObject({
            backgroundColor: RHColor.background
        })

        this.titleLabel.text = "Kõik hanked"
        this.filterChipBar.chips = this._visibleStatusFilterButtons()

        // Every narrowing chip belongs to the same OR selection;
        // "Kõik hanked" clears all (and is the accent chip when nothing is selected).
        // Clicking an already-active chip removes it (toggle).
        this.filterAllButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this._clearQuickFilters()
        }
        for (const chip of this._quickFilterDefinitions()) {
            chip.button.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
                this._toggleFilter(chip.key)
            }
        }

        // The panel is the single source of truth for open/closed; the chip only
        // requests a toggle and mirrors the resulting state. The funnel + caret
        // label and the neutral "soft" open look come from the chip itself.
        this.moreFiltersButton.titleLabel.text = ProcurementSearchViewController._moreFiltersLabelMarkup(NO)
        this.moreFiltersButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.filterPanel.isExpanded = NO == this.filterPanel.isExpanded
        }
        this.filterPanel.isExpandedDidChange = (expanded) => {
            this.moreFiltersButton.chipOpen = expanded
            this.moreFiltersButton.titleLabel.text =
                ProcurementSearchViewController._moreFiltersLabelMarkup(expanded)
            this.filterChipBar.setNeedsLayout()
        }

        this.filterPanel.filterCriteriaDidChange = (criteria) => {
            this._filterCriteria = criteria
            this.moreFiltersButton.chipActive = Object.keys(criteria).length > 0
            this._applyFilters()
        }

        // CPV search: configure the shared picker for the search context (its own
        // heading, "Otsi" confirm, "Sulge" close, and multi-select), then wire the
        // button + reset and install it under the table's search bar.
        this.cpvTreeDialog.dialogTitle = "Otsi CPV-koodi järgi"
        this.cpvTreeDialog.confirmButtonTitle = "Otsi"
        this.cpvTreeDialog.cancelButtonTitle = "Sulge"
        this.cpvTreeDialog.allowsMultipleSelection = YES

        this.cpvSearchBar.openButtonWasPressed = () => {
            this._openCPVTreeForSearch()
        }
        this.cpvSearchBar.clearButtonWasPressed = () => {
            this._clearCPVFilter()
        }
        this.dataView.searchAccessoryView = this.cpvSearchBar

        // The server searches and sorts across all rows; the table asks for the pages it is about to show.
        this.dataView.cooperativeSortFilterRequestDidChange = () => {
            this._scheduleServerQuery()
        }
        this.dataView.didChangeVisibleRowRange = (firstRowIndex, lastRowIndex) => {
            this._visibleRowRange = { first: firstRowIndex, last: lastRowIndex }
            this._loadMissingPagesInVisibleRange()
        }

        this._configureDataView()
        this._updateChipsAppearance()
        this._updateCPVFilterUI()
    }


    // ── Lifecycle ─────────────────────────────────────────────────────────────

    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (
            event.name === RHCore.broadcastEventName.favoritesDidChange ||
            event.name === RHCore.broadcastEventName.applicationModeDidChange
        ) {
            // The stars and the offerer-specific status text are drawn from this state, so the rows are drawn again.
            this._displayLoadedProcurements()
            this._applyFilters()
        }
    }


    override async viewWillAppear() {
        await super.viewWillAppear()
        this._isViewActive = YES
        RHCore.sharedInstance.procurementStore.subscribe(this, () => {
            void this._reloadData(this._pageIndexesNearViewport())
        })
        void this._reloadData()
    }


    override async viewDidAppear() {
        await super.viewDidAppear()
        // Read-only nav badge refresh + unread-message map reload. Also picks up
        // purely time-based count changes (deadline crossings) no push covers —
        // the same on-entry pair every main list view calls.
        RHCore.sharedInstance.notificationStore.refreshGlobalCounts()
        RHCore.sharedInstance.notificationStore.reloadOfferMessageCounts()
    }


    override async viewWillDisappear() {
        await super.viewWillDisappear()
        this._isViewActive = NO
        this._loadGeneration += 1
        clearTimeout(this._serverQueryTimer)
        this._serverQueryTimer = undefined
        RHCore.sharedInstance.procurementStore.unsubscribe(this)
    }


    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)

        const component = route.componentWithName(ProcurementSearchViewController.routeComponentName)
        const searchText = component?.parameters[
            ProcurementSearchViewController.ParameterIdentifierName.searchText
        ]
        if (searchText !== undefined) {
            this._applyRouteSearchText(searchText)
        }
    }


    // ── Data loading ──────────────────────────────────────────────────────────

    // Replaces what is loaded with the first page of the current query and the pages in pageIndexesToKeep
    // (the ones around the viewport on a live update, so the user keeps their place).
    async _reloadData(pageIndexesToKeep: number[] = []) {
        const loadGeneration = ++this._loadGeneration
        const query = this._currentQuery()
        this._latestRequestedQuery = query
        this._isReloading = YES
        this._pageIndexesLoading.clear()
        if (NO == this._hasLoadedProcurements) {
            this.dataView.loading = YES
        }
        try {
            const cpvLabelsPromise = RHCPVCodeFormatter.autocompleteStrings().catch(error => {
                console.error("ProcurementSearchViewController: failed to load CPV labels", error)
            })

            void this._loadProcuringCompanyNames()

            const firstPage = await this._retrievePage(query, 0)
            const otherPageIndexes = [...new Set(pageIndexesToKeep)]
                .filter(pageIndex => pageIndex > 0 && pageIndex * PROCUREMENT_SEARCH_PAGE_SIZE < firstPage.totalMatching)
            const otherPages = await Promise.all(otherPageIndexes.map(async pageIndex => ({
                pageIndex, page: await this._retrievePage(query, pageIndex)
            })))
            await cpvLabelsPromise
            if (loadGeneration !== this._loadGeneration) {
                return
            }

            this.dataView.noDataMessage = "Hankeid ei leitud."
            this._loadedRowsByIndex = new Map()
            this._loadedPageIndexes = new Set()
            this._storePage(0, firstPage)
            otherPages.forEach(({ pageIndex, page }) => this._storePage(pageIndex, page))
            this._totalProcurements = firstPage.totalMatching
            this._chipCounts = firstPage.chipCounts
            this._hasLoadedProcurements = YES
            this._isReloading = NO
            this._displayLoadedProcurements()
            this.dataView.loading = NO
            this.view.setNeedsLayout()
        }
        catch (error) {
            if (loadGeneration !== this._loadGeneration) {
                return
            }
            console.error("ProcurementSearchViewController: failed to load procurements", error)
            this._loadedRowsByIndex = new Map()
            this._loadedPageIndexes = new Set()
            this._totalProcurements = 0
            this._chipCounts = undefined
            this._latestRequestedQuery = undefined
            this._isReloading = NO
            this.dataView.loading = NO
            this.dataView.noDataMessage = "Andmete laadimine ebaõnnestus."
            this._displayLoadedProcurements()
            this.view.setNeedsLayout()
        }
    }


    // Not part of the pages: the store holds the names until a change makes them stale, so a reload costs nothing.
    async _loadProcuringCompanyNames() {
        try {
            this._procuringCompanyNames = await RHCore.sharedInstance.procurementStore.retrievePublishedProcurerNames(
                PUBLISHED_STATUSES, YES
            )
        }
        catch (error) {
            console.error("ProcurementSearchViewController: failed to load the procurer names", error)
        }
    }


    async _loadPage(pageIndex: number) {
        const query = this._latestRequestedQuery
        // While a reload or a query change is pending these rows are about to be replaced, so more of them would be wasted.
        if (!query || this._isReloading || this._serverQueryTimer !== undefined ||
            this._loadedPageIndexes.has(pageIndex) || this._pageIndexesLoading.has(pageIndex)) {
            return
        }
        const loadGeneration = this._loadGeneration
        this._pageIndexesLoading.add(pageIndex)
        try {
            const page = await this._retrievePage(query, pageIndex)
            if (loadGeneration !== this._loadGeneration) {
                return
            }
            // A different count means rows were added or removed since, which shifts every later page.
            if (page.totalMatching !== this._totalProcurements) {
                void this._reloadData(this._pageIndexesNearViewport())
                return
            }
            this._storePage(pageIndex, page)
            this._displayLoadedProcurements()
            this.view.setNeedsLayout()
        }
        catch (error) {
            if (loadGeneration === this._loadGeneration) {
                console.error("ProcurementSearchViewController: failed to load page " + pageIndex, error)
            }
        }
        finally {
            if (loadGeneration === this._loadGeneration) {
                this._pageIndexesLoading.delete(pageIndex)
            }
        }
    }


    _loadMissingPagesInVisibleRange() {
        this._pageIndexesNearViewport().forEach(pageIndex => {
            void this._loadPage(pageIndex)
        })
    }


    // The visible rows plus a page of rows on either side, so the next page is on its way before the user reaches it.
    _pageIndexesNearViewport(): number[] {
        const range = this._visibleRowRange
        if (!range || this._totalProcurements === 0) {
            return []
        }
        const firstRowIndex = Math.max(range.first - PROCUREMENT_SEARCH_PAGE_SIZE, 0)
        const lastRowIndex = Math.min(range.last + PROCUREMENT_SEARCH_PAGE_SIZE, this._totalProcurements - 1)
        const pageIndexes: number[] = []
        for (let pageIndex = Math.floor(firstRowIndex / PROCUREMENT_SEARCH_PAGE_SIZE);
             pageIndex <= Math.floor(lastRowIndex / PROCUREMENT_SEARCH_PAGE_SIZE);
             pageIndex++) {
            pageIndexes.push(pageIndex)
        }
        return pageIndexes
    }


    _retrievePage(query: RHPublishedProcurementsQuery, pageIndex: number): Promise<RHPublishedProcurementsPage> {
        return RHCore.sharedInstance.procurementStore.retrievePublishedProcurementsPage({
            statuses: PUBLISHED_STATUSES,
            shouldIncludeOwnCompanyProcurements: YES,
            query,
            offset: pageIndex * PROCUREMENT_SEARCH_PAGE_SIZE,
            limit: PROCUREMENT_SEARCH_PAGE_SIZE
        })
    }


    _storePage(pageIndex: number, page: RHPublishedProcurementsPage) {
        page.rows.forEach((row, rowIndex) => {
            this._loadedRowsByIndex.set(pageIndex * PROCUREMENT_SEARCH_PAGE_SIZE + rowIndex, row)
        })
        this._loadedPageIndexes.add(pageIndex)
    }


    // Everything the server needs to answer for the screen as it is now.
    _currentQuery(): RHPublishedProcurementsQuery {
        const cooperativeRequest = this.dataView.cooperativeSortFilterRequest
        const criteria = this._filterCriteria
        const query: RHPublishedProcurementsQuery = {
            searchText: cooperativeRequest.searchText,
            sortingInstructions: cooperativeRequest.sortingInstructions,
            quickFilterKeys: [...this._quickFilters.selectedKeys],
            favoriteProcurementIds: RHCore.sharedInstance.favoriteProcurementIds(),
            cpvCodes: this._cpvFilterCodes
        }

        const companyNameCriterion = criteria.procuringCompanyName as RHTextCriterion | undefined
        if (companyNameCriterion?.text.trim()) {
            query.procuringCompanyNameText = companyNameCriterion.text
        }
        const responsibleProcuringUserCriterion = criteria.responsibleProcuringUser as RHTextCriterion | undefined
        if (responsibleProcuringUserCriterion?.text.trim()) {
            query.responsibleProcuringUserSearchText = responsibleProcuringUserCriterion.text
        }
        const costCriterion = criteria.estimatedCost as RHRangeCriterion | undefined
        if (costCriterion) {
            query.estimatedCostRange = { min: costCriterion.min, max: costCriterion.max }
        }
        query.publishedAtRange = ProcurementSearchViewController._instantRangeForDateCriterion(
            criteria.publishedAt as RHDateRangeCriterion | undefined
        )
        query.deadlineDateRange = ProcurementSearchViewController._instantRangeForDateCriterion(
            criteria.deadlineDate as RHDateRangeCriterion | undefined
        )
        return query
    }


    // The picked days are inclusive, so the range runs from the start of the first to the end of the last.
    static _instantRangeForDateCriterion(criterion?: RHDateRangeCriterion): { from?: string, to?: string } | undefined {
        if (!criterion || (!criterion.from && !criterion.to)) {
            return undefined
        }
        const range: { from?: string, to?: string } = {}
        if (criterion.from) {
            range.from = RHFilterPanelView._startOfDay(criterion.from).toISOString()
        }
        if (criterion.to) {
            range.to = RHFilterPanelView._endOfDay(criterion.to).toISOString()
        }
        return range
    }


    // Typing waits for a pause; every other change is asked for at once.
    _scheduleServerQuery() {
        if (!this._isViewActive) {
            return
        }
        clearTimeout(this._serverQueryTimer)
        let delayMilliseconds = 0
        if (this.dataView.cooperativeSortFilterRequest.searchText !== this._latestRequestedQuery?.searchText) {
            delayMilliseconds = PROCUREMENT_SEARCH_QUERY_DELAY_MILLISECONDS
        }
        this._serverQueryTimer = setTimeout(() => {
            this._serverQueryTimer = undefined
            this._reloadIfQueryHasChanged()
        }, delayMilliseconds)
    }


    _reloadIfQueryHasChanged() {
        const previousQuery = this._latestRequestedQuery
        if (previousQuery && JSON.stringify(this._currentQuery()) === JSON.stringify(previousQuery)) {
            // Pages held back while this change was pending may still be missing from the viewport.
            this._loadMissingPagesInVisibleRange()
            return
        }
        void this._reloadData()
    }


    _applyRouteSearchText(searchText: string) {
        if (this.dataView.searchTextField.text === searchText) {
            return
        }

        this.dataView.searchTextField.text = searchText
        if (this.dataView.tableView.isMemberOfViewTree) {
            void this.dataView.updateTableDataByFiltering()
        }
    }


    // A filter changed: the server answers for all rows, and the table follows once it has.
    _applyFilters() {
        this._scheduleServerQuery()
    }


    // One table row per matching procurement, so the scroll height is right before any page has arrived.
    _displayLoadedProcurements() {
        const rows: ProcurementSearchDataItem[] = new Array(this._totalProcurements)
        for (let rowIndex = 0; rowIndex < this._totalProcurements; rowIndex++) {
            rows[rowIndex] = this._loadedRowsByIndex.get(rowIndex) ?? this._placeholderRowAtIndex(rowIndex)
        }
        this.dataView.data = rows
        this._updateChipLabels()
    }


    _placeholderRowAtIndex(rowIndex: number): ProcurementSearchPlaceholderRow {
        if (!this._placeholderRows[rowIndex]) {
            this._placeholderRows[rowIndex] = { _id: "placeholder-" + rowIndex, isPlaceholder: YES }
        }
        return this._placeholderRows[rowIndex]
    }


    // ── CPV search ──────────────────────────────────────────────────────────────

    private _openCPVTreeForSearch() {
        void this.cpvTreeDialog.show(
            this._cpvFilterCodes,
            (selectedCodes: string[]) => {
                this._cpvFilterCodes = selectedCodes
                    .map(code => code.trim())
                    .filter(code => code.length > 0)
                this._updateCPVFilterUI()
                this._applyFilters()
            }
        )
    }


    private _clearCPVFilter() {
        if (this._cpvFilterCodes.length === 0) {
            return
        }
        this._cpvFilterCodes = []
        this._updateCPVFilterUI()
        this._applyFilters()
    }


    // Refreshes the CPV button's label + active state (and its reset button) to
    // reflect the current filter. Called whenever the selected CPV codes change.
    private _updateCPVFilterUI() {
        if (this._cpvFilterCodes.length === 0) {
            this.cpvSearchBar.label = RHProcurementSearchViewTokens.cpvSearchButtonLabel
            this.cpvSearchBar.active = NO
        }
        else if (this._cpvFilterCodes.length === 1) {
            this.cpvSearchBar.label = `CPV: ${this._cpvFilterCodes[0]}`
            this.cpvSearchBar.active = YES
        }
        else {
            this.cpvSearchBar.label = `CPV: ${this._cpvFilterCodes.length} koodi`
            this.cpvSearchBar.active = YES
        }
        this.cpvSearchBar.setNeedsLayout()
        this.view.setNeedsLayout()
    }


    // ── Multi-select chip state ─────────────────────────────────────────────────

    _quickFilterDefinitions(): { button: RHFilterChipButton, key: ProcurementSearchQuickFilterKey, baseLabel: string }[] {
        return [
            { button: this.filterStartedButton, key: "started", baseLabel: "Alustatud" },
            { button: this.filterEvaluationButton, key: "evaluating", baseLabel: "Hindamisel" },
            { button: this.filterFinalizedButton, key: "lopetatud", baseLabel: "Lõpetatud" },
            { button: this.filterInExecutionButton, key: "inExecution", baseLabel: "Täitmisel" },
            { button: this.filterExecutedButton, key: "executed", baseLabel: "Teostatud" },
            { button: this.filterFavoritesButton, key: "favorites", baseLabel: "Lemmikud" }
        ]
    }


    _toggleFilter(key: ProcurementSearchQuickFilterKey) {
        this._quickFilters.toggleKey(key)
        this._updateChipsAppearance()
        this._applyFilters()
    }


    _clearQuickFilters() {
        this._quickFilters.selectedKeys.clear()
        this._updateChipsAppearance()
        this._applyFilters()
    }


    // Active chips carry the green accent; "Kõik hanked" is active exactly when
    // nothing else is selected.
    _updateChipsAppearance() {
        this.filterAllButton.chipActive = this._quickFilters.selectedKeys.size === 0
        for (const chip of this._quickFilterDefinitions()) {
            chip.button.chipActive = this._quickFilters.selectedKeys.has(chip.key)
        }
    }


    _updateChipLabels() {
        const chipCounts = this._chipCounts
        this.filterAllButton.titleLabel.text = this._chipLabelWithCount("Kõik hanked", chipCounts?.all)
        for (const chip of this._quickFilterDefinitions()) {
            chip.button.titleLabel.text = this._chipLabelWithCount(chip.baseLabel, chipCounts?.[chip.key])
        }
        this.filterChipBar.setNeedsLayout()
        this.view.setNeedsLayout()
    }


    // Count appended as PLAIN text (no HTML span — see RHFilterChipButton sizing); absent until the server has counted.
    _chipLabelWithCount(label: string, count?: number): string {
        if (count === undefined) {
            return label
        }
        return `${label} · ${count}`
    }


    private _visibleStatusFilterButtons() {
        // Same global set for everyone; "Muud filtrid" sits last, favorites just before it.
        return [
            this.filterAllButton,
            this.filterStartedButton,
            this.filterEvaluationButton,
            this.filterFinalizedButton,
            this.filterInExecutionButton,
            this.filterExecutedButton,
            this.filterFavoritesButton,
            this.moreFiltersButton
        ]
    }


    // ── Data view configuration ───────────────────────────────────────────────

    _configureDataView() {
        this.dataView.descriptors = ProcurementSearchViewController._descriptorsBlankForPlaceholders([
            // 0 — Favorite star (lemmik)
            RHFavoriteColumn.descriptorWithProcurementIdProvider<ProcurementSearchRow>(
                dataItem => dataItem._id?.toString()
            ),
            // 1 — Viitenumber
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: "referenceNumber",
                title: "Viitenumber",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, procurement) => {
                    cellView.titleLabel!.text = procurement.referenceNumber ?? "-"
                }
            }),
            // 2 — Nimetus (procurement title; never labelled "Pealkiri" here)
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: "title",
                title: "Nimetus",
                buttonWasPressed: (_cellView, procurement) => this._openProcurementDetails(procurement),
                initCell: cellView => cellView.configureAsTextLinkWithNormalTextColor(RHColor.textBlack),
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, procurement) => {
                    cellView.titleLabel!.text = RHTextSanitizer.escapedHTML(
                        procurement.title ?? "Nimetus puudub"
                    )
                    cellView.configureAsClampedMultilineText()
                }
            }),
            // 3 — Ettevõte. The direct presentation field keeps built-in
            // search and sorting reliable because CBDataView indexes direct keys.
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: "procuringCompanyName",
                title: "Ettevõte",
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, procurement) => {
                    cellView.titleLabel!.text = procurement.procuringCompany?.name ?? "-"
                }
            }),
            // 4 — CPV
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: "parts",
                title: "CPV",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.left,
                cellLabelAlignment: UITextView.textAlignment.left,
                updateCell: (cellView, procurement) => {
                    cellView.titleLabel!.text = this._cpvCellTextForProcurement(procurement)
                }
            }),
            // 5 — Eeldatav maksumus (sum of per-part estimated costs; right-aligned)
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: RHNumberSorting.valueKeyPath,
                title: "Eeldatav maksumus (KM-ta)",
                allowSorting: YES,
                dataType: "number",
                sortingInstructions: orderingState => RHNumberSorting.instructions(orderingState),
                titleLabelAlignment: UITextView.textAlignment.right,
                cellLabelAlignment: UITextView.textAlignment.right,
                updateCell: (cellView, procurement) => {
                    const parts = procurement.parts ?? []
                    const costs = parts
                        .map(part => part.estimatedCost)
                        .filter((cost): cost is number => typeof cost === "number")
                    if (costs.length === 0) {
                        cellView.titleLabel!.text = "-"
                        return
                    }
                    const total = costs.reduce((sum: number, cost: number) => sum + cost, 0)
                    cellView.titleLabel!.text = RHEuroFormatter.formattedAmount(total)
                }
            }),
            // 6 — Avaldatud (publication date; newest first by default)
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: "publishedAt",
                title: "Avaldatud",
                allowSorting: YES,
                initialOrderingState: "descending",
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, procurement) => {
                    const publishedAt = procurement.publishedAt
                    cellView.titleLabel!.isSingleLine = NO
                    if (publishedAt) {
                        cellView.titleLabel!.text = formatRHDateTimeWithLineBreak(new Date(publishedAt))
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                    }
                }
            }),
            // 7 — Tähtaeg (submission deadline; muted once it has passed)
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: "deadlineDate",
                title: "Tähtaeg",
                allowSorting: YES,
                dataType: "string",
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, procurement) => {
                    const deadline = procurement.deadlineDate
                    cellView.titleLabel!.isSingleLine = NO
                    if (deadline) {
                        const deadlineDate = new Date(deadline)
                        cellView.titleLabel!.text = formatRHDateTimeWithLineBreak(deadlineDate)
                        if (deadlineDate < new Date()) {
                            cellView.titleLabel!.textColor = RHColor.textMuted
                        }
                        else {
                            cellView.titleLabel!.textColor = RHColor.textBlack
                        }
                    }
                    else {
                        cellView.titleLabel!.text = "-"
                        cellView.titleLabel!.textColor = RHColor.textBlack
                    }
                }
            }),
            // 8 — Osade arv (part count)
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: "",
                title: "Osade arv",
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, procurement) => {
                    cellView.titleLabel!.text = (procurement.parts?.length ?? 0) + ""
                }
            }),
            // 9 — Seisund
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow>({
                keyPath: "lifecycleStateText",
                title: "Seisund",
                minimumWidth: RHProcurementSearchViewTokens.columnMinimumWidths.status,
                allowSorting: YES,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, procurement) => {
                    let secondaryText: string | undefined
                    let secondaryColor: UIColor | undefined
                    if (RHCore.sharedInstance.isInOffererMode) {
                        const presentation = RHProcurementLifecyclePresentation.presentationForOffererRow(procurement)
                        secondaryText = presentation.secondaryText
                        secondaryColor = presentation.secondaryColor
                    }
                    cellView.titleLabel!.configureWithObject({
                        isSingleLine: NO,
                        text: RHProcurementLifecyclePresentation.twoLineCellHTML(
                            RHProcurementLifecyclePresentation.textForLifecycleState(procurement.lifecycleState),
                            secondaryText,
                            secondaryColor
                        ),
                        textColor: RHProcurementLifecyclePresentation.colorForLifecycleState(procurement.lifecycleState),
                        style: { whiteSpace: "normal" }
                    })
                }
            }),
            // 10 — Vaata (opens the procurement details; mirrors the dashboard action button)
            CBDataViewCellDescriptorWithObject<ProcurementSearchRow, RHTableActionButtonView>({
                keyPath: "_id",
                title: "Vaata",
                minimumWidth: RHProcurementSearchViewTokens.columnMinimumWidths.action,
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                cellLabelAlignment: UITextView.textAlignment.center,
                initCustomView: () => new RHTableActionButtonView().configuredWithObject({
                    preferredButtonWidth: 116,
                    preferredButtonHeight: 32
                }),
                updateCell: (cellView, procurement) => {
                    cellView.customView.text = "Vaata"
                    cellView.customView.buttonWasPressed = () => {
                        this._openProcurementDetails(procurement)
                    }
                }
            })
        ])
        this.dataView.tableHeaderView.cellWeights = PROCUREMENT_SEARCH_COLUMN_WEIGHTS
        this.dataView.tableView.cellWeights = PROCUREMENT_SEARCH_COLUMN_WEIGHTS
    }


    // A row whose page has not arrived is blank: its cells are cleared and their custom views hidden.
    static _descriptorsBlankForPlaceholders(
        descriptors: CBColumnDescriptor<ProcurementSearchRow>[]
    ): CBColumnDescriptor<ProcurementSearchDataItem>[] {
        return descriptors.map(descriptor => {
            if (isCBSelectionColumnDescriptor(descriptor)) {
                throw new Error("The procurement search table has no selection column.")
            }
            return {
                ...descriptor,
                updateCell: (cellView: ProcurementSearchCellView, dataItem: ProcurementSearchDataItem) => {
                    cellView.buttonView?.configureWithObject({ enabled: dataItem.isPlaceholder !== YES })
                    if (cellView.customView) {
                        cellView.customView.hidden = dataItem.isPlaceholder === YES
                    }
                    if (dataItem.isPlaceholder === YES) {
                        if (cellView.titleLabel) {
                            cellView.titleLabel.text = ""
                        }
                        return
                    }
                    descriptor.updateCell?.(cellView, dataItem)
                }
            } as CBDataViewCellDescriptor<ProcurementSearchDataItem>
        })
    }


    // ── Navigation ────────────────────────────────────────────────────────────

    _openProcurementDetails(procurement: ProcurementSearchRow) {
        // Remove this search component from the route and hand control to the
        // details controller. Without removing "procurement_search" the root
        // router (which picks the first matching registered controller) would
        // re-resolve to this search screen, so the details would never show.
        // backTo lets the details back button return here.
        UIRoute.currentRoute
            .routeWithViewControllerComponent(
                ProcurementDetailsViewController,
                {
                    id: procurement._id + "",
                    backTo: ProcurementSearchViewController.routeComponentName
                }
            )
            .routeByRemovingComponentNamed(ProcurementSearchViewController.routeComponentName)
            .apply()
    }


    // ── Private helpers ───────────────────────────────────────────────────────

    private _cpvCodesForProcurement(procurement: ProcurementSearchRow): string[] {
        return (procurement.parts ?? [])
            .map(part => part.CPV)
            .filter((cpv): cpv is string => typeof cpv === "string" && cpv.trim().length > 0)
            .map((cpv: string) => cpv.trim())
    }


    private _cpvCellTextForProcurement(procurement: ProcurementSearchRow): string {
        const cpvCodes = this._cpvCodesForProcurement(procurement)
        if (cpvCodes.length === 0) {
            return "-"
        }

        const cpvDescriptions = new Set(
            cpvCodes
                .map(cpvCode => RHCPVCodeFormatter.descriptionForCPVUsingLoadedData(cpvCode))
                .filter(description => description.length > 0)
        )
        if (cpvDescriptions.size === 0) {
            return "-"
        }
        return [...cpvDescriptions].join("; ")
    }


    private static _makeFilterChip(title: string): RHFilterChipButton {
        // The chip owns all of its styling (flat surface, ghost/active/soft modes);
        // the factory only seeds the label. The owner drives active/open.
        return new RHFilterChipButton().configuredWithObject({
            titleLabel: { text: title }
        })
    }


    // "Muud filtrid" label: funnel icon + text + a caret that flips when its panel
    // is open. The chip's titleLabel renders this markup as HTML, so the inline
    // SVGs inherit the chip's text colour via currentColor.
    private static _moreFiltersLabelMarkup(expanded: boolean): string {
        let caret = RHBadges.filterChip.caretCollapsedMarkup
        if (expanded) {
            caret = RHBadges.filterChip.caretExpandedMarkup
        }
        return RHBadges.filterChip.funnelIconMarkup + "Muud filtrid" + caret
    }

    // ── Layout ────────────────────────────────────────────────────────────────

    override layoutViewSubviews() {
        super.layoutViewSubviews()

        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.view.contentBoundsWithInset(padding)

        bounds
            .rectangleByAddingY(RHSpacing.xLarge)
            .rectangleWithHeight(labelHeight)
            .assignedAsFrameOfView(this.titleLabel)
            // The chip bar wraps its chips across as many rows as the width needs
            // and reports its own height, so the row can never run off the page.
            .rectangleForNextRow(padding, this.filterChipBar)
            .assignedAsFrameOfView(this.filterChipBar)
            .rectangleForNextRow(padding, this.filterPanel)
            .assignedAsFrameOfView(this.filterPanel)
            // The "Otsi CPV-koodi järgi" chip lives inside the data view, directly
            // under its search bar (see RHDataView.searchAccessoryView).
            .rectangleForNextRow(padding, RHTables.dataViewPageHeight)
            .assignedAsFrameOfView(this.dataView)
    }

}


// ─────────────────────────────────────────────────────────────────────────────
// CPVSearchBarView — the small blue "Otsi CPV-koodi järgi" button (plus a reset
// button shown while a CPV filter is active) placed directly under the global search
// bar (installed as RHDataView.searchAccessoryView). A thin full-width row that
// left-aligns its controls; the owner drives the label / active state and is notified
// when the button or its reset is pressed. Widths come from each button's own label
// (the stable measurement), so the row never feeds a hover-resize loop.
// ─────────────────────────────────────────────────────────────────────────────

class CPVSearchBarView extends UIView {

    readonly openButton = new RHButton(this.elementID + "OpenButton")
    readonly clearButton = new RHButton(this.elementID + "ClearButton")

    /** Called when the main CPV button is pressed. */
    openButtonWasPressed?: () => void
    /** Called when the reset button is pressed. */
    clearButtonWasPressed?: () => void

    private _active = NO

    constructor(elementID?: string) {
        super(elementID)

        this.openButton.addedAsSubviewToView(this)
        this.openButton.configureWithObject({
            titleLabel: {
                text: RHProcurementSearchViewTokens.cpvSearchButtonLabel,
                style: { fontSize: RHButtons.compactFontSize }
            },
            variant: "primary",
            contentPadding: RHButtons.compactContentPadding
        })
        this.openButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.openButtonWasPressed?.()
        }

        this.clearButton.configureWithObject({
            titleLabel: {
                text: "✕",
                style: { fontSize: RHButtons.compactFontSize }
            },
            variant: "softDanger",
            contentPadding: RHButtons.compactContentPadding,
            hoverText: "Lähtesta CPV-otsing"
        })
        this.clearButton.controlEventTargetAccumulator.PrimaryActionTriggered = () => {
            this.clearButtonWasPressed?.()
        }
    }

    set label(text: string) {
        this.openButton.configureWithObject({ titleLabel: { text } })
    }

    set active(value: boolean) {
        this._active = value
        if (value) {
            this.clearButton.addedAsSubviewToView(this)
        }
        else {
            this.clearButton.removeFromSuperview()
        }
        this.setNeedsLayout()
    }

    override intrinsicContentHeight(_constrainingWidth = 0): number {
        return RHProcurementSearchViewTokens.cpvSearchButtonHeight
    }

    override layoutSubviews() {
        super.layoutSubviews()

        const height = RHProcurementSearchViewTokens.cpvSearchButtonHeight

        const openButtonFrame = this.contentBoundsWithInset(0)
            .rectangleWithHeight(height)
            .rectangleWithWidth(CPVSearchBarView._measuredButtonWidth(this.openButton, height))
            .assignedAsFrameOfView(this.openButton)

        if (this._active) {
            openButtonFrame
                .rectangleForNextColumn(
                    RHSpacing.small,
                    CPVSearchBarView._measuredButtonWidth(this.clearButton, height)
                )
                .assignedAsFrameOfView(this.clearButton)
        }
    }

    // Width from the button's own label (the stable measurement) plus content padding
    // and a small optical buffer — never the button's unreliable intrinsic width.
    private static _measuredButtonWidth(button: RHButton, height: number): number {
        return button.titleLabel.intrinsicContentWidth(height)
            + RHButtons.compactContentPadding * 2
            + RHSpacing.small
    }

}
